// app/components/TripShareButton.tsx
'use client';

/**
 * Share a trip as a view-only link. One tap on Share does the lot:
 *
 *   Share → (save the trip, if it isn't yet) → make the link → sheet
 *
 *   ┌ Share this trip ─────────────────────────────── ✕ ┐  desktop: rises
 *   │ They get the day-by-day plan. No account needed.   │  from the action
 *   │ ┌──────────── photo ─────┬──── little map ──────┐ │  bar; phones: a
 *   │ │ Coast run              │ coast, every stop's  │ │  bottom sheet
 *   │ │ 1,019 km · 2 days ·    │ pin (A B C, the map's│ │
 *   │ │ 1 night                │ own pins and tags)   │ │  (frozen when
 *   │ └────────────────────────┴──────────────────────┘ │  Share is tapped)
 *   │ 🔗 campingaustralia.com.au/…/abc123/coast-run      │  one line
 *   │ [ Copy link ]  → [ ✓ Copied ]  (the one signal)   │
 *   │ [Messages] [WhatsApp] [Email] [More]   phones      │  More = system
 *   │ [WhatsApp] [Email] [Facebook] [QR] [More] desktop │  share sheet
 *   │ 👁 View only · anyone with the link     Change     │  → Turn off link
 *   └───────────────────────────────────────────────────┘
 *
 * The QR tile swaps the card for the code, at the card's size (the sheet
 * never grows), with the trip's name under it and a PNG to print.
 *
 * Anyone with the link sees the day sheet; no account needed. Turning it
 * off kills the link for good; sharing again mints a new one.
 *
 * In the planner the trip may not be saved yet: pass `ensureSaved` and it
 * runs first (it returns the saved trip's id, or null when it couldn't).
 * Guests (`isLoggedIn === false`) get `onNeedSignIn` instead: a link needs
 * the trip on the server.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BRAND, withBase } from '../lib/brand';
import { AU_COAST } from './planner/auMap';
import { ROLE_HEX, type StopRole } from './planner/roles';
import { encodeQr, qrSvgPath } from '../utils/qr';
import { apiGetShareState, apiRevokeShare, apiShareTrip } from '../utils/tripApi';
import {
  IconCheck,
  IconCopy,
  IconDownload,
  IconFacebook,
  IconLink,
  IconMail,
  IconMessage,
  IconMore,
  IconShare,
  IconWhatsApp,
} from './planner/icons';
import { Button, Dialog, buttonClasses } from './planner/ui';

/** "Melbourne to Sydney" → "melbourne-to-sydney" (at most 48 chars). Pure. */
export function tripSlug(name?: string | null): string {
  return (name || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '');
}

/**
 * Build the link on this brand's own origin (the server's guess is a
 * fallback). The trip's name rides along as a readable last segment
 * (/trip/shared/abc123/melbourne-to-sydney); the page ignores it.
 */
export function shareUrlFor(token: string, serverUrl?: string | null, tripName?: string | null): string {
  const slug = tripSlug(tripName);
  if (typeof window !== 'undefined' && token) {
    const path = withBase(`/trip/shared/${token}${slug ? `/${slug}` : ''}`);
    return `${BRAND.siteUrl}${path}`;
  }
  return serverUrl ? `${serverUrl.replace(/\/+$/, '')}${slug ? `/${slug}` : ''}` : '';
}

/** "https://www.trip-planner.com.au/trip/shared/abc" → "trip-planner.com.au/trip/shared/abc" */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/^www\./, '');
}

/**
 * The link as people check it before sending: the whole path, no host
 * ("/trip/shared/abc123/melbourne-to-sydney"). Pure.
 */
export function sharePathLabel(url: string): string {
  const bare = displayUrl(url);
  const cut = bare.indexOf('/');
  return cut >= 0 ? bare.slice(cut) : bare;
}

/**
 * The link on one line, as people read it:
 * "campingaustralia.com.au/…/melbourne-to-sydney". The host and the trip's
 * name are the parts people check; the token between them folds into "…".
 * Pure.
 */
export function shortShareUrl(url: string, max = 40): string {
  const bare = displayUrl(url);
  const named = bare.match(/^([^/]+)\/.*\/shared\/[^/?#]+\/([^/?#]+)$/);
  if (named) return `${named[1]}/…/${named[2]}`;
  if (bare.length <= max) return bare;
  const m = bare.match(/^([^/]+)\/.*\/shared\/([^/?#]+)$/);
  return m ? `${m[1]}/…/${m[2]}` : bare;
}

/**
 * The link split the way the sheet draws it on one line:
 *   campingaustralia.com.au/…/aaajzxvd5nb/melbourne-to-sydney
 *   host (ink)             (muted) token (ink) slug (muted, truncates)
 * The plumbing between host and token folds into "…". Pure.
 */
export function shareLinkParts(url: string): { host: string; token: string; slug: string } | null {
  const bare = displayUrl(url);
  const m = bare.match(/^([^/]+)\/(?:.*\/)?shared\/([^/?#]+)(?:\/([^/?#]+))?\/?$/);
  if (!m) return null;
  return { host: m[1], token: m[2], slug: m[3] || '' };
}

export type ShareTargetKey = 'sms' | 'whatsapp' | 'email' | 'facebook';
export type ShareTileKey = ShareTargetKey | 'more' | 'qr';

/**
 * One-tap hand-offs for the apps people actually send trips in. Pure, so
 * the encoded links are testable. `sms:` uses `?&body=` (works on iOS and
 * Android alike).
 */
export function shareTargets(url: string, tripName?: string): { key: ShareTargetKey; label: string; href: string }[] {
  const name = tripName?.trim() || 'Our road trip';
  const text = `${name}: ${url}`;
  return [
    { key: 'sms', label: 'Messages', href: `sms:?&body=${encodeURIComponent(text)}` },
    { key: 'whatsapp', label: 'WhatsApp', href: `https://wa.me/?text=${encodeURIComponent(text)}` },
    {
      key: 'email',
      label: 'Email',
      href: `mailto:?subject=${encodeURIComponent(name)}&body=${encodeURIComponent(`Here's the plan, day by day:\n${url}`)}`,
    },
    { key: 'facebook', label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  ];
}

/**
 * The hand-offs for this device. Phones: Messages · WhatsApp · Email ·
 * More (More opens the phone's own share sheet; without one it copies the
 * link). Desktop: WhatsApp · Email · Facebook · QR code (the trip opens on
 * your phone with one scan), then More when the browser has a system share
 * sheet (Safari, Edge, Chrome on Windows). Pure.
 */
export function shareTargetsFor(
  url: string,
  tripName: string | undefined,
  opts: { touch: boolean; nativeShare: boolean }
): { key: ShareTileKey; label: string; href?: string }[] {
  const all = shareTargets(url, tripName);
  const pick = (k: ShareTargetKey) => all.find((t) => t.key === k)!;
  const more = { key: 'more' as const, label: 'More' };
  if (opts.touch) return [pick('sms'), pick('whatsapp'), pick('email'), more];
  return [pick('whatsapp'), pick('email'), pick('facebook'), { key: 'qr' as const, label: 'QR code' }, ...(opts.nativeShare ? [more] : [])];
}

/** What the recipient will see, drawn small in the share sheet. */
export interface SharePreview {
  /** The destination's photo (or the trip's hero). */
  photoUrl?: string | null;
  /** The road line, start to end. */
  path?: { lat: number; lng: number }[] | null;
  /** "1,019 km · 2 days" */
  figures?: string;
  /** Start and end names, labelled on the little map. */
  from?: string;
  to?: string;
  /** How many stops: the end marker wears that letter (3 → "C"). */
  stopCount?: number;
  /**
   * Every located stop, in order, with its role: the little map pins each
   * one (A, B, C) in the main map's colours. Without it, start and end only.
   */
  stops?: { lat: number; lng: number; name: string; role: StopRole; letter: string }[];
}

/**
 * A route line fitted into a box, for the preview card. Equirectangular
 * with the longitude squeezed by cos(latitude), so the shape is true. Also
 * hands back the projection, so the coastline lands under the same line.
 * Pure.
 */
export function fitRoutePath(
  path: { lat: number; lng: number }[],
  w: number,
  h: number,
  pad = 14
): { d: string; a: [number, number]; b: [number, number]; project: (lng: number, lat: number) => [number, number] } | null {
  const pts = path.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (pts.length < 2) return null;
  const step = Math.max(1, Math.floor(pts.length / 240));
  const thin = pts.filter((_, i) => i % step === 0 || i === pts.length - 1);
  const midLat = thin.reduce((sum, p) => sum + p.lat, 0) / thin.length;
  const k = Math.cos((midLat * Math.PI) / 180);
  const xs = thin.map((p) => p.lng * k);
  const ys = thin.map((p) => -p.lat);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = Math.max(maxX - minX, 1e-6);
  const spanY = Math.max(maxY - minY, 1e-6);
  const scale = Math.min((w - pad * 2) / spanX, (h - pad * 2) / spanY);
  const ox = (w - spanX * scale) / 2;
  const oy = (h - spanY * scale) / 2;
  const r1 = (n: number) => Math.round(n * 10) / 10;
  const project = (lng: number, lat: number): [number, number] => [r1(ox + (lng * k - minX) * scale), r1(oy + (-lat - minY) * scale)];
  const at = (i: number): [number, number] => project(thin[i].lng, thin[i].lat);
  const d = thin.map((_, i) => `${i ? 'L' : 'M'}${at(i).join(' ')}`).join('');
  return { d, a: at(0), b: at(thin.length - 1), project };
}

/** The coastline under a fitted route, as one SVG path. Pure. */
export function coastPath(project: (lng: number, lat: number) => [number, number]): string {
  return AU_COAST.map(
    (ring) => ring.map(([lng, lat], i) => `${i ? 'L' : 'M'}${project(lng, lat).join(' ')}`).join('') + 'Z'
  ).join('');
}

/** "C" for the third stop. */
const letterFor = (n: number) => String.fromCharCode(64 + Math.max(1, Math.min(26, n)));

/**
 * A photo that's mostly night (mean luminance under ~22%) reads as a failed
 * load at card size. Sampled on a 12px canvas; CORS-blocked images count as
 * fine (we can't tell).
 */
// Each photo is measured once per page: re-opening the sheet (or anything
// re-rendering it) never flashes the card back to "checking".
const brightCache = new Map<string, boolean>();

function usePhotoBrightEnough(url: string | null | undefined): boolean | null {
  const [ok, setOk] = useState<boolean | null>(() => (url ? brightCache.get(url) ?? null : false));
  useEffect(() => {
    if (!url) {
      setOk(false);
      return;
    }
    const known = brightCache.get(url);
    if (known !== undefined) {
      setOk(known);
      return;
    }
    let live = true;
    const settle = (v: boolean) => {
      brightCache.set(url, v);
      if (live) setOk(v);
    };
    setOk(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = 12;
        c.height = 12;
        const ctx = c.getContext('2d');
        if (!ctx) return settle(true);
        ctx.drawImage(img, 0, 0, 12, 12);
        const px = ctx.getImageData(0, 0, 12, 12).data;
        let sum = 0;
        for (let i = 0; i < px.length; i += 4) sum += 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
        settle(sum / (px.length / 4) / 255 >= 0.22);
      } catch {
        settle(true);
      }
    };
    img.onerror = () => {
      // No CORS headers: we can't measure it. Shown if it loads at all.
      const plain = new Image();
      plain.onload = () => settle(true);
      plain.onerror = () => settle(false);
      plain.src = url;
    };
    img.src = url;
    return () => {
      live = false;
    };
  }, [url]);
  return ok;
}

/** A name tag on the little map: the main map's chip (white card, 1px edge, ink 600). */
function MapLabel({ x, y, w, text }: { x: number; y: number; w: number; text: string }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={15} rx={3.5} fill="#ffffff" stroke="rgba(22,35,58,0.16)" strokeWidth={0.75} />
      <text x={x + 5} y={y + 10.7} fontSize={9.5} fontWeight={600} fill="#16233a" style={{ fontFamily: 'inherit' }}>
        {text}
      </text>
    </g>
  );
}

/** The main map's teardrop pin, small: tip on the point, letter in the head. */
function MapPin({ x, y, letter, role }: { x: number; y: number; letter: string; role: StopRole }) {
  return (
    <g transform={`translate(${x - 8.25} ${y - 21.5}) scale(0.55)`}>
      <path
        d="M15 39c-.6 0-1.1-.3-1.4-.8C9 31.6 2 23.4 2 14.5 2 7 7.8 1.5 15 1.5S28 7 28 14.5c0 8.9-7 17.1-11.6 23.7-.3.5-.8.8-1.4.8z"
        fill={ROLE_HEX[role]}
        stroke="#ffffff"
        strokeWidth={2.5}
      />
      <text x={15} y={18.6} textAnchor="middle" fontSize={13} fontWeight={700} fill="#ffffff" style={{ fontFamily: 'inherit' }}>
        {letter}
      </text>
    </g>
  );
}

/**
 * Where each stop's name tag goes on the little map: right of its pin when
 * there's room, else left, else left out (never on top of another tag or
 * pin). The destination is placed first, then the start, then the rest.
 * Pure.
 */
export function placeMapLabels(
  pins: { x: number; y: number; name: string }[],
  W: number,
  H: number
): ({ x: number; y: number; w: number; text: string } | null)[] {
  type Box = { l: number; t: number; r: number; b: number };
  // Every pin, its own included: the head and the narrow tail down to the tip.
  const pinBoxes: Box[] = pins.flatMap((p) => [
    { l: p.x - 8, t: p.y - 22, r: p.x + 8, b: p.y - 8 },
    { l: p.x - 3, t: p.y - 8, r: p.x + 3, b: p.y },
  ]);
  const taken: Box[] = [];
  const overlaps = (r: Box, o: Box) => r.l < o.r && r.r > o.l && r.t < o.b && r.b > o.t;
  const hit = (r: Box) => taken.some((o) => overlaps(r, o)) || pinBoxes.some((o) => overlaps(r, o));
  const order = pins.map((_, i) => i);
  if (order.length > 1) {
    const last = order.pop() as number;
    order.unshift(last);
  }
  const out: ({ x: number; y: number; w: number; text: string } | null)[] = pins.map(() => null);
  order.forEach((i) => {
    const p = pins[i];
    const text = p.name.length > 16 ? `${p.name.slice(0, 15)}…` : p.name;
    const w = Math.round(text.length * 5.5 + 10);
    const t = p.y - 7.5;
    // Slid back inside the card rather than dropped at its edge.
    const box = (l: number, top: number): Box => {
      const x = Math.max(3, Math.min(W - 3 - w, l));
      const y = Math.max(3, Math.min(H - 18, top));
      return { l: x, t: y, r: x + w, b: y + 15 };
    };
    const candidates = [
      box(p.x + 5, t), // right of the tip, where the main map puts it
      box(p.x - 5 - w, t), // left
      box(p.x - w / 2, p.y + 4), // under the tip
      box(p.x - w / 2, p.y - 40), // over the head
    ];
    const spot = candidates.find((c) => !hit(c)) ?? null;
    if (!spot) return;
    taken.push(spot);
    out[i] = { x: spot.l, y: spot.t, w, text };
  });
  return out;
}

/**
 * The 2:1 card: what the person you send it to will open. Memoised on its
 * (frozen) props: copying the link, the QR toggle or the parent's own
 * re-renders never re-draw or re-fetch it.
 */
const PreviewCard = React.memo(function PreviewCard({ name, preview }: { name: string; preview: SharePreview }) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const bright = usePhotoBrightEnough(preview.photoUrl && !photoFailed ? preview.photoUrl : null);
  // No photo, a dark one, or one still being checked: the card is the map
  // alone, never a black box. A bright photo slides in once it's vetted.
  const photo = preview.photoUrl && !photoFailed && bright === true ? preview.photoUrl : null;
  const H = 176;
  const W = photo ? 160 : 352;
  // Map alone: the trip's name sits bottom-left over the map, so the route
  // and its pins are fitted into the band above it, never under the words.
  const mapH = photo ? H : H - 44;
  // Fit the line AND every stop, so a pin is never off the edge.
  const fitPts = [...(preview.path || []), ...(preview.stops || []).map((st) => ({ lat: st.lat, lng: st.lng }))];
  const line = preview.path && preview.path.length > 1 ? fitRoutePath(fitPts.length > 1 ? fitPts : preview.path, W, mapH, photo ? 26 : 24) : null;
  const routeD = line && preview.path ? fitRoutePathWith(preview.path, line.project) : '';
  const coast = line ? coastPath(line.project) : '';
  const pins = line
    ? preview.stops && preview.stops.length
      ? preview.stops.map((st) => {
          const [x, y] = line.project(st.lng, st.lat);
          return { x, y, name: st.name, role: st.role, letter: st.letter };
        })
      : [
          { x: line.a[0], y: line.a[1], name: preview.from || '', role: 'start' as StopRole, letter: 'A' },
          { x: line.b[0], y: line.b[1], name: preview.to || '', role: 'end' as StopRole, letter: letterFor(preview.stopCount ?? 2) },
        ]
    : [];
  const labels = placeMapLabels(pins, W, mapH);
  return (
    <div
      className="relative flex aspect-[2/1] w-full overflow-hidden rounded-tp bg-[#d9e7f3] ring-1 ring-inset ring-tp-line"
      data-testid="share-preview"
      data-photo={photo ? 'yes' : 'no'}
      data-pins={pins.length}
    >
      {photo && (
        <div className="relative h-full min-w-0 flex-[1.2] bg-tp-ink">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={photo}
            alt=""
            className="block h-full w-full object-cover animate-tp-fade motion-reduce:animate-none"
            onError={() => setPhotoFailed(true)}
          />
          <span
            aria-hidden="true"
            className="absolute inset-0 bg-[linear-gradient(0deg,rgba(0,0,0,0.6)_0%,rgba(0,0,0,0.2)_50%,rgba(0,0,0,0)_75%)]"
          />
        </div>
      )}
      <div className="relative h-full min-w-0 flex-1" data-testid="share-preview-map">
        {line && (
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="absolute inset-0 h-full w-full" aria-hidden="true">
            <path d={coast} fill="#f3f0e8" stroke="#bccbdb" strokeWidth={0.8} strokeLinejoin="round" />
            <path d={routeD} fill="none" stroke="#ffffff" strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
            <path d={routeD} fill="none" stroke="#1a68d1" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" />
            {labels.map((l, i) => (l && pins[i].name ? <MapLabel key={`l${i}`} {...l} /> : null))}
            {pins
              .map((p, i) => ({ p, i }))
              .reverse()
              .map(({ p, i }) => (
                <MapPin key={`p${i}`} x={p.x} y={p.y} letter={p.letter} role={p.role} />
              ))}
          </svg>
        )}
        {!photo && (
          <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-16 bg-[linear-gradient(0deg,rgba(255,255,255,0.92),rgba(255,255,255,0))]" />
        )}
      </div>
      <div className={`absolute bottom-0 left-0 z-[1] px-3 pb-2.5 ${photo ? 'max-w-[56%]' : 'max-w-[70%]'}`}>
        <p
          className={`m-0 truncate text-[15px] font-semibold leading-5 ${photo ? '!text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.4)]' : '!text-tp-ink'}`}
        >
          {name}
        </p>
        {preview.figures && (
          <p className={`m-0 truncate text-tp-sm tabular-nums ${photo ? '!text-white/90' : '!text-tp-muted'}`}>{preview.figures}</p>
        )}
      </div>
    </div>
  );
});

/** The route line drawn with an existing projection (so it shares the stops' fit). Pure. */
export function fitRoutePathWith(path: { lat: number; lng: number }[], project: (lng: number, lat: number) => [number, number]): string {
  const pts = path.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  const step = Math.max(1, Math.floor(pts.length / 240));
  return pts
    .filter((_, i) => i % step === 0 || i === pts.length - 1)
    .map((p, i) => `${i ? 'L' : 'M'}${project(p.lng, p.lat).join(' ')}`)
    .join('');
}

/** "Scan to open on your phone": the link as a QR code, drawn crisp at any size. */
/** The QR code's quiet zone, in modules: the spec's 4, so every camera reads it. */
export const QR_QUIET = 4;

/** "Coast run" → "coast-run-qr.png". Pure. */
export function qrFilename(tripName?: string | null): string {
  return `${tripSlug(tripName) || 'trip'}-qr.png`;
}

/**
 * The QR code as a printable PNG: the code with its full quiet zone, the
 * trip's name under it and the site's host under that. For the glovebox
 * sheet or the fridge.
 */
function downloadQrPng(matrix: boolean[][], tripName: string, host: string) {
  const px = 12;
  const n = matrix.length + QR_QUIET * 2;
  const size = n * px;
  const band = 96;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size + band;
  const ctx = c.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.fillStyle = '#16233a';
  matrix.forEach((row, y) =>
    row.forEach((on, x) => {
      if (on) ctx.fillRect((x + QR_QUIET) * px, (y + QR_QUIET) * px, px, px);
    })
  );
  const font = getComputedStyle(document.body).fontFamily || 'system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#16233a';
  ctx.font = `600 30px ${font}`;
  let title = tripName;
  while (title.length > 4 && ctx.measureText(title).width > size - 48) title = `${title.slice(0, -2)}…`;
  ctx.fillText(title, size / 2, size + 26);
  ctx.fillStyle = '#5b6b82';
  ctx.font = `500 20px ${font}`;
  ctx.fillText(host, size / 2, size + 62);
  c.toBlob((blob) => {
    if (!blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = qrFilename(tripName);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }, 'image/png');
}

/**
 * "Scan to open on your phone". It takes the preview card's place, at the
 * card's exact size, so the sheet never grows: the code (full 4-module
 * quiet zone) with the trip's name under it, and a PNG to print.
 */
function ShareQr({ url, tripName }: { url: string; tripName: string }) {
  const matrix = React.useMemo(() => encodeQr(url), [url]);
  const qr = React.useMemo(() => (matrix ? qrSvgPath(matrix, QR_QUIET) : null), [matrix]);
  if (!qr || !matrix) return null;
  const host = shareLinkParts(url)?.host ?? displayUrl(url).split('/')[0];
  return (
    <div
      className="flex aspect-[2/1] w-full items-center gap-4 overflow-hidden rounded-tp bg-tp-surface p-3 ring-1 ring-inset ring-tp-line animate-tp-fade motion-reduce:animate-none"
      data-testid="share-qr"
    >
      <figure className="m-0 flex h-full flex-none flex-col items-center justify-center rounded-tp-sm bg-white px-2 pb-1.5 pt-1 shadow-[0_1px_2px_rgba(22,35,58,0.1)]">
        <svg
          viewBox={`0 0 ${qr.n} ${qr.n}`}
          className="block aspect-square h-[calc(100%-18px)] w-auto"
          shapeRendering="crispEdges"
          role="img"
          aria-label={`QR code that opens ${tripName}`}
        >
          <rect width={qr.n} height={qr.n} fill="#ffffff" />
          <path d={qr.d} fill="#16233a" />
        </svg>
        <figcaption className="max-w-[140px] truncate text-[11px] font-semibold leading-4 text-tp-ink">{tripName}</figcaption>
      </figure>
      <div className="min-w-0">
        <p className="m-0 text-tp-base font-semibold text-tp-ink">Open it on your phone</p>
        <p className="m-0 mt-1 text-tp-sm text-tp-muted">Point the camera at the code. No app needed.</p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => downloadQrPng(matrix, tripName, host)}
          icon={<IconDownload className="h-4 w-4" />}
          className="mt-3"
          data-testid="share-qr-download"
        >
          Download PNG
        </Button>
      </div>
    </div>
  );
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'linking' }
  | { kind: 'ready'; tripId: string; url: string }
  | { kind: 'off'; tripId: string }
  | { kind: 'error'; message: string };

export interface TripShareButtonProps {
  /** A saved trip's id. Optional when `ensureSaved` is given. */
  tripId?: string | null;
  /** Save the trip first (planner). Resolves to its server id, or null. */
  ensureSaved?: () => Promise<string | null>;
  /** false → Share asks for sign-in instead of opening. */
  isLoggedIn?: boolean;
  onNeedSignIn?: () => void;
  /** For the link card and the share sheet's title. */
  tripName?: string;
  /** 'bar': the planner footer button (40/44px). 'chip': a small row action. */
  variant?: 'bar' | 'chip';
  /** Bar only: 'primary' once the trip is saved (Share is the next step). */
  emphasis?: 'primary' | 'secondary';
  disabled?: boolean;
  /** Tooltip for the disabled state. */
  disabledReason?: string;
  /** Draws the card the recipient will see (photo, route line, figures). */
  preview?: SharePreview;
  /** The sheet opened or closed (the planner rests its bar behind it). */
  onOpenChange?: (open: boolean) => void;
}

function IconEye({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.75" />
    </svg>
  );
}

function IconQr({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="3.5" width="6.5" height="6.5" rx="1" />
      <rect x="14" y="3.5" width="6.5" height="6.5" rx="1" />
      <rect x="3.5" y="14" width="6.5" height="6.5" rx="1" />
      <path d="M14 14h2.5v2.5H14zM18 18h2.5v2.5H18zM14 19.5h1.5M19.5 14v1.5" />
    </svg>
  );
}

/** Phones and tablets: the share sheet and the Messages hand-off make sense. */
function useTouch(): boolean {
  const [touch, setTouch] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    setTouch(window.matchMedia('(pointer: coarse)').matches);
  }, []);
  return touch;
}

export default function TripShareButton({
  tripId,
  ensureSaved,
  isLoggedIn,
  onNeedSignIn,
  tripName,
  variant = 'chip',
  emphasis = 'secondary',
  disabled = false,
  disabledReason,
  preview,
  onOpenChange,
}: TripShareButtonProps) {
  const [open, setOpen] = useState(false);
  const openChangeRef = useRef(onOpenChange);
  openChangeRef.current = onOpenChange;
  useEffect(() => {
    openChangeRef.current?.(open);
  }, [open]);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const firstRef = useRef<HTMLButtonElement>(null);
  const runRef = useRef(0);
  const copiedTimer = useRef<number | undefined>(undefined);
  const touch = useTouch();
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  // The card is frozen at the moment Share is tapped: saving the trip (new
  // stop ids, photos re-keyed) or copying the link never re-draws it.
  const [shownPreview, setShownPreview] = useState<SharePreview | undefined>(undefined);
  // Measure the photo before the sheet opens, so the card arrives whole.
  usePhotoBrightEnough(variant === 'bar' ? preview?.photoUrl ?? null : null);
  // Desktop: the sheet rises from the action bar it was opened from.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const anchorRef = useRef<HTMLElement | null>(null);

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const run = useCallback(async () => {
    const seq = ++runRef.current;
    setNote(null);
    setCopied(false);
    setSettingsOpen(false);
    setConfirmOff(false);
    let id = tripId ?? null;
    if (ensureSaved) {
      setPhase({ kind: 'saving' });
      id = await ensureSaved();
      if (seq !== runRef.current) return;
      if (!id) {
        setPhase({ kind: 'error', message: 'Couldn’t save the trip, so there’s no link yet.' });
        return;
      }
    }
    if (!id) {
      setPhase({ kind: 'error', message: 'Save the trip first. Then share it.' });
      return;
    }
    setPhase({ kind: 'linking' });
    // Re-use a live link: the people who already have it keep it.
    const current = await apiGetShareState(id);
    const state = current?.shared && current.token ? current : await apiShareTrip(id);
    if (seq !== runRef.current) return;
    if (!state?.token) {
      setPhase({ kind: 'error', message: 'Couldn’t make a link just now.' });
      return;
    }
    setPhase({ kind: 'ready', tripId: id, url: shareUrlFor(state.token, state.share_url, tripName) });
  }, [tripId, ensureSaved, tripName]);

  const onTrigger = () => {
    if (disabled) return;
    if (isLoggedIn === false) {
      onNeedSignIn?.();
      return;
    }
    setShownPreview(preview);
    anchorRef.current =
      variant === 'bar' ? ((triggerRef.current?.closest('[data-testid="trip-actions"]') as HTMLElement | null) ?? triggerRef.current) : null;
    setQrOpen(false);
    setSettingsOpen(false);
    setOpen(true);
    void run();
  };

  const close = () => {
    runRef.current++;
    setQrOpen(false);
    setOpen(false);
    setPhase({ kind: 'idle' });
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setNote(null);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 4000);
    } catch {
      // No clipboard access: show the whole link, selected, to copy by hand.
      setShowFull(true);
      window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      setNote('Press Ctrl+C (or ⌘C) to copy the selected link.');
    }
  };

  const nativeShare = async (url: string) => {
    // No share sheet (most desktops, some in-app browsers): copy instead.
    if (!canNativeShare) {
      await copy(url);
      return;
    }
    try {
      await navigator.share({ title: tripName || 'Our road trip', url });
    } catch {
      /* dismissed */
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    const ok = await apiRevokeShare(id);
    setBusy(false);
    setConfirmOff(false);
    if (ok) {
      setPhase({ kind: 'off', tripId: id });
      setNote(null);
    } else {
      setNote('Couldn’t turn the link off. Try again.');
    }
  };

  const remake = async (id: string) => {
    setBusy(true);
    const s = await apiShareTrip(id);
    setBusy(false);
    if (s?.token) {
      setSettingsOpen(false);
      setPhase({ kind: 'ready', tripId: id, url: shareUrlFor(s.token, s.share_url, tripName) });
    } else setNote('Couldn’t make a link just now. Try again.');
  };

  const primary = variant === 'bar' && emphasis === 'primary';
  const trigger =
    variant === 'bar' ? (
      <Button
        ref={triggerRef}
        variant={primary ? 'primary' : 'secondary'}
        size="md"
        onClick={onTrigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        title={disabled ? disabledReason : 'Share a view-only link'}
        icon={<IconShare className="h-4 w-4 flex-none" />}
        className={`!h-11 md:!h-10 ${primary ? 'min-w-0 flex-1' : 'flex-none !border-[#94a3b8] !px-4 hover:!border-tp-muted md:!px-3.5'}`}
        data-testid="trip-share"
      >
        Share
      </Button>
    ) : (
      <button
        type="button"
        onClick={onTrigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-md border border-solid border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:border-gray-300 disabled:opacity-50"
        data-testid="trip-share"
      >
        <IconShare className="h-3.5 w-3.5" /> Share
      </button>
    );

  const working = phase.kind === 'saving' || phase.kind === 'linking' || phase.kind === 'idle';
  // Copy leads everywhere; the system share sheet is the "More" tile.
  const targets = phase.kind === 'ready' ? shareTargetsFor(phase.url, tripName, { touch, nativeShare: canNativeShare }) : [];
  const name = tripName || 'Your trip';
  const card = shownPreview && (shownPreview.photoUrl || (shownPreview.path && shownPreview.path.length > 1)) ? shownPreview : null;
  // Every tile's icon gets one treatment: 20px, the tile's own ink.
  const TILE_ICON = 'h-5 w-5 flex-none';

  return (
    <>
      {trigger}
      <Dialog
        open={open}
        onClose={close}
        title={phase.kind === 'off' ? 'Link turned off' : 'Share this trip'}
        description={
          phase.kind === 'off'
            ? 'Anyone who had it now sees nothing. Make a new link any time.'
            : 'They get the day-by-day plan. No account needed.'
        }
        initialFocusRef={phase.kind === 'ready' ? (firstRef as React.RefObject<HTMLElement>) : undefined}
        anchorRef={variant === 'bar' ? (anchorRef as React.RefObject<HTMLElement>) : undefined}
        testId="share-dialog"
      >
        <div className="mt-4" aria-live="polite">
          {working && (
            <div
              className="flex h-[68px] items-center gap-2.5 rounded-tp border border-solid border-tp-line bg-tp-surface px-3.5 text-tp-base text-tp-muted"
              aria-busy="true"
            >
              <span
                className="h-4 w-4 flex-none animate-spin rounded-full border-2 border-solid border-tp-line-strong border-t-tp-brand motion-reduce:animate-none"
                aria-hidden="true"
              />
              {phase.kind === 'saving' ? 'Saving your trip…' : 'Making a link…'}
            </div>
          )}
          {phase.kind === 'error' && (
            <div
              className="flex items-center gap-3 rounded-tp border border-solid border-tp-danger/25 bg-tp-danger-soft px-3 py-2.5 text-tp-base text-tp-danger"
              role="alert"
            >
              <span className="min-w-0 flex-1">{phase.message}</span>
              <Button variant="secondary" size="sm" onClick={() => void run()} className="flex-none">
                Try again
              </Button>
            </div>
          )}
          {phase.kind === 'ready' && (
            <>
              {/* The card they'll get, or the QR code in its exact place:
                  the sheet never changes height. */}
              {qrOpen ? <ShareQr url={phase.url} tripName={name} /> : card ? <PreviewCard name={name} preview={card} /> : null}
              {/* The link on one line, as people read it: the site, "…", the
                  link's own code, the trip's name muted. The full address is
                  the tooltip (and what Copy copies). */}
              <div
                className={`flex h-11 items-center gap-2.5 rounded-tp border border-solid border-tp-line bg-tp-surface px-3 ${qrOpen || card ? 'mt-2.5' : ''}`}
                title={phase.url}
              >
                <IconLink className="h-4 w-4 flex-none text-tp-muted" />
                <span className="sr-only">Share link</span>
                <input
                  ref={inputRef}
                  readOnly
                  value={phase.url}
                  onFocus={(e) => e.currentTarget.select()}
                  data-testid="share-link"
                  aria-label="Share link"
                  tabIndex={showFull ? 0 : -1}
                  className={
                    showFull
                      ? 'box-border block h-8 min-w-0 flex-1 border-0 bg-transparent p-0 text-tp-sm text-tp-ink [font-family:inherit] focus:outline-none'
                      : 'sr-only'
                  }
                  style={{ margin: 0 }}
                />
                {!showFull && <ShortLink url={phase.url} />}
              </div>

              <Button
                ref={firstRef}
                variant="primary"
                size="lg"
                onClick={() => void copy(phase.url)}
                icon={copied ? <IconCheck className="tp-tick h-4 w-4" /> : <IconCopy className="h-4 w-4" />}
                className={`mt-3 w-full md:!h-10 ${copied ? '!border-tp-ok !bg-tp-ok hover:!bg-tp-ok' : ''}`}
                aria-live="polite"
                data-testid="share-copy"
                {...(copied ? { 'data-copied': '' } : {})}
              >
                {copied ? 'Copied' : 'Copy link'}
              </Button>

              {/* Where people actually send a trip, one tap each. One look
                  for every tile: a 20px glyph in ink over its word. */}
              <ul
                className="m-0 mt-2 grid list-none gap-2 p-0"
                style={{ gridTemplateColumns: `repeat(${Math.max(3, targets.length)}, minmax(0, 1fr))` }}
              >
                {targets.map((t) => {
                  const cls = buttonClasses(
                    'secondary',
                    'lg',
                    `w-full no-underline !h-16 flex-col !gap-1 !px-1 text-tp-sm ${t.key === 'qr' && qrOpen ? '!border-tp-brand/40 !bg-tp-brand-soft !text-tp-brand' : ''}`
                  );
                  const icon =
                    t.key === 'sms' ? (
                      <IconMessage className={TILE_ICON} />
                    ) : t.key === 'whatsapp' ? (
                      <IconWhatsApp className={TILE_ICON} />
                    ) : t.key === 'email' ? (
                      <IconMail className={TILE_ICON} />
                    ) : t.key === 'facebook' ? (
                      <IconFacebook className={TILE_ICON} />
                    ) : t.key === 'qr' ? (
                      <IconQr className={TILE_ICON} />
                    ) : (
                      <IconMore className={TILE_ICON} />
                    );
                  return (
                    <li key={t.key} className="min-w-0">
                      {t.href ? (
                        <a
                          href={t.href}
                          target={t.key === 'whatsapp' || t.key === 'facebook' ? '_blank' : undefined}
                          rel={t.key === 'whatsapp' || t.key === 'facebook' ? 'noopener noreferrer' : undefined}
                          className={cls}
                          data-testid={`share-${t.key}`}
                        >
                          {icon}
                          <span className="max-w-full truncate">{t.label}</span>
                        </a>
                      ) : t.key === 'qr' ? (
                        <button
                          type="button"
                          onClick={() => setQrOpen((v) => !v)}
                          aria-pressed={qrOpen}
                          className={cls}
                          data-testid="share-qr-toggle"
                        >
                          {icon}
                          <span className="max-w-full truncate">{t.label}</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void nativeShare(phase.url)}
                          className={cls}
                          title={canNativeShare ? 'Your device’s share options' : 'Copies the link'}
                          data-testid={`share-${t.key}`}
                        >
                          {icon}
                          <span className="max-w-full truncate">{t.label}</span>
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* Only when something needs saying (no clipboard, a failed
                  turn-off). A good copy is said by the button alone. */}
              {note && (
                <p role="status" className="m-0 mt-2 text-tp-sm text-tp-muted" data-testid="share-note">
                  {note}
                </p>
              )}

              {/* Who can open it, said in one line, with the way to change it. */}
              <div className="mt-3 border-0 border-t border-solid border-tp-line pt-3">
                <div className="flex min-h-[32px] items-center gap-2" data-testid="share-access">
                  <IconEye className="h-4 w-4 flex-none text-tp-muted" />
                  <p className="m-0 min-w-0 flex-1 truncate text-tp-sm text-tp-ink">
                    <span className="font-semibold">View only</span>
                    <span className="text-tp-muted"> · anyone with the link</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSettingsOpen((v) => !v);
                      setConfirmOff(false);
                    }}
                    aria-expanded={settingsOpen}
                    className="inline-flex h-8 min-h-0 flex-none items-center rounded-tp-sm border-0 bg-transparent px-2 text-tp-sm font-semibold text-tp-brand transition-colors duration-150 hover:bg-tp-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/50 [font-family:inherit]"
                    data-testid="share-settings"
                  >
                    {settingsOpen ? 'Done' : 'Change'}
                  </button>
                </div>
                {settingsOpen && (
                  <div className="mt-2 animate-tp-enter motion-reduce:animate-none">
                    {confirmOff ? (
                      <div className="rounded-tp border border-solid border-tp-danger/25 bg-tp-danger-soft p-3" role="alertdialog" aria-label="Turn off the link?">
                        <p className="m-0 text-tp-sm text-tp-ink">Turn the link off? Anyone who has it will see nothing.</p>
                        <div className="mt-2.5 flex gap-2">
                          <Button variant="secondary" size="sm" onClick={() => setConfirmOff(false)}>
                            Keep it
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void revoke(phase.tripId)}
                            disabled={busy}
                            className="!border-tp-danger/45 !text-tp-danger hover:!bg-white"
                            data-testid="share-revoke"
                          >
                            Turn off link
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-3 rounded-tp bg-tp-surface px-3 py-2">
                        <p className="m-0 min-w-0 text-tp-sm text-tp-muted">Nobody can edit it. Turn the link off to stop sharing.</p>
                        <Button variant="danger" size="sm" onClick={() => setConfirmOff(true)} className="flex-none !text-tp-danger">
                          Turn off link
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {phase.kind === 'off' && (
            <div className="flex items-center justify-between gap-3">
              <p className="m-0 min-w-0 text-tp-sm text-tp-muted" role="status">
                {note ?? ''}
              </p>
              <Button variant="secondary" size="md" onClick={() => void remake(phase.tripId)} disabled={busy}>
                Make a new link
              </Button>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}

/**
 * The link on one line: "campingaustralia.com.au/…/aaajzxvd5nb/coast-run".
 * The site and the link's code in ink, the plumbing folded into a muted
 * "…", the trip's name muted and the first thing to go when space is short.
 */
function ShortLink({ url }: { url: string }) {
  const parts = shareLinkParts(url);
  if (!parts) {
    return (
      <span className="min-w-0 flex-1 truncate text-tp-sm text-tp-ink" data-testid="share-link-short">
        {displayUrl(url)}
      </span>
    );
  }
  return (
    <span className="flex min-w-0 flex-1 items-baseline whitespace-nowrap text-tp-sm leading-5" data-testid="share-link-short">
      <span className="flex-none font-medium text-tp-ink">{parts.host}</span>
      <span className="flex-none text-tp-faint">/…/</span>
      <span className="flex-none font-semibold text-tp-ink">{parts.token}</span>
      {parts.slug && <span className="min-w-0 truncate text-tp-muted">/{parts.slug}</span>}
    </span>
  );
}
